import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Terminal, ChevronLeft, ChevronRight, CheckCircle2, Mail } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import usePageview, { getVisitorSession } from "@/lib/usePageview";

// Phase 25.16ax — Pricing page reframed.
//
// Previous incarnation showed 4 specific SaaS tiers (Starter $299 / Scale
// $1499 / Professional $3499 / Enterprise quote) on the public web. Founder
// pulled all visible pricing 2026-06-02: "the price tag for starters is
// scaring people away. So we should remove all the pricing." Replaced
// with a conversation-led framing — pricing happens on a call after we
// understand the operator's scale, regulatory surface, and use-case.
//
// Single carve-out: POS Live SMB tier remains visibly priced ($50/mo
// starting) on its own dedicated page at /pos-live — built for the
// self-serve hospitality / SMB conversion path where transparency wins.

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Pricing() {
  usePageview();
  const [form, setForm] = useState({
    email: "", name: "", company: "", role: "", scale: "", message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email) {
      toast.error("Work email is required");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/industry-leads`, {
        email: form.email,
        name: form.name,
        company: form.company,
        role: form.role,
        message: [
          form.scale ? `Scale: ${form.scale}` : "",
          form.message ? `Notes: ${form.message}` : "",
        ].filter(Boolean).join("\n"),
        industry: "wagering",
        source: "pricing-page",
        visitor_session: getVisitorSession(),
      });
      setSubmitted(true);
      toast.success("Received — the DARA team will reach out within one business day.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed — try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white" data-testid="pricing-page">
      {/* NAV */}
      <nav className="sticky top-0 z-50 border-b border-zinc-900 bg-black/90 backdrop-blur">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-3">
          <Link to="/" className="flex items-center gap-2" data-testid="pricing-brand-back">
            <ChevronLeft className="w-4 h-4 text-zinc-500" />
            <Terminal className="w-5 h-5 text-[#FFB000]" />
            <span className="font-heading font-black text-xl tracking-tighter">DARA</span>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/login" data-testid="pricing-nav-login" className="font-mono text-[11px] uppercase tracking-widest text-zinc-300 hover:text-white px-3 py-2">Login</Link>
            <a
              href="#contact"
              data-testid="pricing-nav-contact-cta"
              className="bg-[#FFB000] text-black font-bold font-mono text-[11px] uppercase tracking-wider px-4 py-2 hover:bg-[#E59E00] transition-colors"
            >
              Talk to the DARA team
            </a>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section className="border-b border-zinc-900 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#10283F]/40 via-transparent to-[#FFB000]/5 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-24 relative">
          <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-[0.25em] text-[#FFB000] mb-4">
            // PRICING · CONVERSATION-LED · NO PUBLIC PRICE LIST
          </div>
          <h1 className="font-heading font-black text-3xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] max-w-5xl">
            Bloomberg doesn't have a public price list. <span className="text-[#FFB000]">Neither do we.</span>
          </h1>
          <p className="mt-8 text-base sm:text-lg text-zinc-300 max-w-3xl leading-relaxed">
            DARA's pricing depends on the size of your operation, the regulators you sit under, the number of pillars you need covered, and whether you want a 90-day proof-of-concept or a multi-year operating-partner arrangement. None of those are the same conversation — so we don't fake it with one price list.
          </p>
          <p className="mt-4 text-base sm:text-lg text-zinc-300 max-w-3xl leading-relaxed">
            What we will tell you up front, before any commitment: a Paid Proof-of-Concept on one pillar typically clears procurement in 2-3 weeks, the fee credits 100% toward an annual contract if you convert, and standard MNDA is in place from minute one. The actual number depends on you — talk to us and we'll quote properly.
          </p>
          <div className="mt-10 flex items-center gap-4 flex-wrap">
            <a
              href="#contact"
              data-testid="pricing-hero-contact-cta"
              className="inline-flex items-center gap-2 bg-[#FFB000] text-black font-bold font-mono text-xs uppercase tracking-widest px-6 py-3 hover:opacity-90"
            >
              Talk to the DARA team <ChevronRight className="w-4 h-4" />
            </a>
            <Link
              to="/pos-live"
              data-testid="pricing-poslive-link"
              className="font-mono text-[11px] uppercase tracking-widest text-zinc-300 hover:text-[#FFB000] underline-offset-4 hover:underline flex items-center gap-1.5"
            >
              <ChevronRight className="w-3.5 h-3.5" /> POS Live — SMB hospitality · visible pricing
            </Link>
          </div>
        </div>
      </section>

      {/* WHY NO PRICE LIST */}
      <section className="border-b border-zinc-900 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000] mb-3">
            // WHY WE DON'T PUBLISH PRICING
          </div>
          <h2 className="font-heading font-black text-2xl sm:text-4xl tracking-tight max-w-5xl leading-tight">
            Pricing without context <span className="text-[#FFB000]">misleads both of us.</span>
          </h2>
          <p className="mt-4 text-sm sm:text-base text-zinc-300 max-w-3xl leading-relaxed">
            Three reasons a single public price tag would do you (and us) a disservice:
          </p>

          <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-px bg-zinc-900 border border-zinc-900" data-testid="pricing-reasons-grid">
            <ReasonBlock
              code="01"
              title="Your scale dictates the deployment"
              body="A 50-staff fire-safety operator and a 5,000-policy contractor-liability insurer are not the same DARA. Different ingestion shape, different rulebook subset, different audit cadence, different support tier. Public pricing would anchor at one of those scales and mismatch the rest."
            />
            <ReasonBlock
              code="02"
              title="Your regulators dictate the pillars"
              body="A wagering operator needs AML + responsible-gambling + integrity + advertising covered. A mining junior needs JORC + work-programme + native-title + competitor watch. The pillar count drives the cost, and we won't know which pillars apply until we've talked."
            />
            <ReasonBlock
              code="03"
              title="The right number is conversation-led"
              body="Half the value of the first call is figuring out which DARA tier actually fits — not selling you the most expensive one. We'd rather quote A$0 (Paid POC fee credits forward) and earn the annual than push a number that turns out to be the wrong shape."
            />
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="border-b border-zinc-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000] mb-3">
                // TALK TO THE DARA TEAM · NO PROCUREMENT GAUNTLET
              </div>
              <h2 className="font-heading font-black text-2xl sm:text-4xl tracking-tight leading-tight">
                30 minutes. <span className="text-[#FFB000]">Quote at the end.</span>
              </h2>
              <p className="mt-4 text-sm sm:text-base text-zinc-300 leading-relaxed">
                The form below goes directly to the DARA team. Within one business day you'll get a calendar link, plus a one-page summary of how DARA would shape for your specific scale. NDA available before the call if you need it.
              </p>
              <div className="mt-6 space-y-2">
                <p className="text-xs sm:text-[11px] text-zinc-500 leading-relaxed font-mono">
                  <Mail className="inline w-3 h-3 mr-1.5 -mt-0.5" /> majisonmanuel@gmail.com · +61 481 172 655
                </p>
              </div>
            </div>

            {submitted ? (
              <div className="border border-[#FFB000]/40 bg-[#FFB000]/5 p-8" data-testid="pricing-form-success">
                <CheckCircle2 className="w-10 h-10 text-[#FFB000] mb-4" />
                <h3 className="font-heading font-bold text-xl tracking-tight mb-2">Received.</h3>
                <p className="text-sm text-zinc-300 leading-relaxed">
                  The DARA team will reach out within one business day with a calendar link and a one-page summary of how DARA shapes for your specific scale.
                </p>
                <p className="mt-4 font-mono text-[11px] uppercase tracking-widest text-zinc-500">
                  // DARA OS · Perth WA
                </p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4" data-testid="pricing-form">
                <Field label="Work email" required>
                  <input
                    type="email" required data-testid="pricing-form-email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full bg-zinc-950 border border-zinc-800 text-white px-4 py-3 font-mono text-sm focus:outline-none focus:border-[#FFB000]"
                    placeholder="you@company.com"
                  />
                </Field>
                <Field label="Your name">
                  <input
                    type="text" data-testid="pricing-form-name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full bg-zinc-950 border border-zinc-800 text-white px-4 py-3 font-mono text-sm focus:outline-none focus:border-[#FFB000]"
                  />
                </Field>
                <Field label="Company">
                  <input
                    type="text" data-testid="pricing-form-company"
                    value={form.company}
                    onChange={(e) => setForm({ ...form, company: e.target.value })}
                    className="w-full bg-zinc-950 border border-zinc-800 text-white px-4 py-3 font-mono text-sm focus:outline-none focus:border-[#FFB000]"
                  />
                </Field>
                <Field label="Role">
                  <input
                    type="text" data-testid="pricing-form-role"
                    value={form.role}
                    onChange={(e) => setForm({ ...form, role: e.target.value })}
                    className="w-full bg-zinc-950 border border-zinc-800 text-white px-4 py-3 font-mono text-sm focus:outline-none focus:border-[#FFB000]"
                  />
                </Field>
                <Field label="Scale of operation" sub="optional · helps us prepare">
                  <input
                    type="text" data-testid="pricing-form-scale"
                    value={form.scale}
                    onChange={(e) => setForm({ ...form, scale: e.target.value })}
                    className="w-full bg-zinc-950 border border-zinc-800 text-white px-4 py-3 font-mono text-sm focus:outline-none focus:border-[#FFB000]"
                    placeholder="e.g. 5 sites · 800 staff · 3 jurisdictions"
                  />
                </Field>
                <Field label="What you're trying to solve" sub="optional · a sentence or two">
                  <textarea
                    rows={4} data-testid="pricing-form-message"
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className="w-full bg-zinc-950 border border-zinc-800 text-white px-4 py-3 font-mono text-sm focus:outline-none focus:border-[#FFB000]"
                  />
                </Field>
                <button
                  type="submit" disabled={submitting} data-testid="pricing-form-submit"
                  className="w-full bg-[#FFB000] text-black font-bold font-mono text-xs uppercase tracking-widest px-6 py-3 hover:opacity-90 disabled:opacity-50"
                >
                  {submitting ? "Sending…" : "Talk to the DARA team →"}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      <footer className="bg-black border-t border-zinc-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
          <p className="text-xs sm:text-[11px] text-zinc-500 leading-relaxed font-mono">
            DARA OS · Manuel Maji · majisonmanuel@gmail.com · +61 481 172 655 · Perth WA
          </p>
        </div>
      </footer>
    </div>
  );
}

function ReasonBlock({ code, title, body }) {
  return (
    <div className="bg-black p-6 sm:p-8 flex flex-col" data-testid={`pricing-reason-${code}`}>
      <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000] mb-3">
        // {code}
      </div>
      <h3 className="font-heading font-bold text-lg tracking-tight mb-3">{title}</h3>
      <p className="text-sm text-zinc-300 leading-relaxed">{body}</p>
    </div>
  );
}

function Field({ label, sub, required, children }) {
  return (
    <div>
      <label className="block font-mono text-[11px] uppercase tracking-widest text-zinc-400 mb-1.5">
        {label}{required && <span className="text-[#FFB000] ml-1">*</span>}
        {sub && <span className="text-zinc-600 ml-2 normal-case tracking-normal italic">{sub}</span>}
      </label>
      {children}
    </div>
  );
}
